import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * v5.48 (FOUNDER: "site wide - when we navigate between tabs - it does not auto
 * scroll to the top - this is a problem").
 *
 * A single-page app does not reload, so nothing resets the scroll position on a
 * route change: tap a nav tab halfway down a long list of caterers and the new
 * page opens halfway down, on content that has nothing to do with where you were.
 * Five pages had each remembered to call `window.scrollTo(0, 0)` on mount, which
 * is the shape that rots — the sixth page never remembers, and this repo has that
 * lesson written down in several places. ONE mechanism, mounted once, above both
 * route trees, so a page cannot be built without it.
 *
 * THREE THINGS IT DELIBERATELY DOES NOT DO, each of which would be a new bug:
 *
 *  - It does not fire on POP. Back and forward are the one case where the browser
 *    already knows better: it restores the position you left, and scrolling to the
 *    top would throw away the place a customer was reading and asked to return to.
 *  - It does not fight a #hash. An anchored link is an explicit request for a
 *    position on the page, and it lands after this effect.
 *  - It does not fire on a SEARCH-STRING change. Filtering a list (?category=,
 *    ?page=, ?q=) is not navigation — yanking the viewport to the top while
 *    somebody works through the filter rail is the thing that reads as broken.
 *    The keying is on `pathname` alone, and that is the whole rule.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  // THE NAVIGATION TYPE IS READ, NEVER DEPENDED ON. It belongs in a ref because
  // it CHANGES ON ITS OWN: a fresh load is 'POP', and the first in-app click
  // flips it to 'PUSH'. With it in the dependency array the effect re-fired on
  // that flip alone — so changing a FILTER (?category=, a search-string-only
  // navigation that must never move the viewport) yanked the page to the top,
  // measured: y=900 -> 0 while the pathname had not changed at all. Keying on
  // `pathname` is the rule; the type only decides what to do when it fires.
  const navTypeRef = useRef(navigationType);
  navTypeRef.current = navigationType;

  useEffect(() => {
    if (navTypeRef.current === 'POP') return undefined;
    if (hash) return undefined;

    // `behavior: 'instant'`, and the exact word is the whole fix. In the CSSOM
    // spec 'auto' does NOT mean "jump" — it means "use the element's computed
    // scroll-behavior", and this app sets `html { scroll-behavior: smooth }`
    // (index.css). So the obvious spelling made the reset a ~500ms ANIMATION
    // that the incoming page's own layout interrupts: measured, /catering
    // (y=1400) -> / sat at 1400 with the effect firing correctly every time.
    // 'instant' is the only value that overrides the stylesheet.
    const toTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } catch {
        window.scrollTo(0, 0);  // older Safari takes only the positional form
      }
      // A belt-and-braces assignment for engines that ignore an unknown
      // behavior value rather than throwing.
      if (window.scrollY !== 0 && document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
    };

    // ONCE IS NOT ENOUGH, and this is the whole reason the naive version fails.
    // The effect runs while the OUTGOING page's height is still on the document:
    // the incoming route has rendered its shell but not its data, so the document
    // briefly shrinks and the browser preserves the old offset as the real
    // content mounts under it — measured, /catering (y=1200) -> / stayed at 1200
    // with the effect firing correctly the whole time. Scroll now for the common
    // case, and again after the next paint, once the new page has its own height.
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
